import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small always-visible strip inside Product Admin that tells Lindy whether
 * her latest edits are safe to deploy. The mechanism (snapshot file that
 * ships in git) is invisible; this widget makes it visible.
 *
 * States:
 *  - loading — checking
 *  - ok      — "Your edits are ready for the next deploy" (green)
 *  - drift   — "N edits are in the DB but not yet in the deploy snapshot"
 *              plus a "Sync now" button that force-writes and re-checks.
 *  - error   — could not read status
 */
export default function SnapshotStatusBar() {
  const [state, setState] = useState({ status: 'loading' });
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products/snapshot-status`);
      setState({ status: 'ok', data });
    } catch (e) {
      setState({ status: 'error', error: e.response?.data?.detail || e.message });
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 30 s in case someone in another tab edits.
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/products/write-snapshot`);
      await refresh();
      toast.success('Snapshot rebuilt — your edits will ride the next deploy');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rebuild snapshot');
    } finally {
      setSyncing(false);
    }
  };

  if (state.status === 'loading' || state.status === 'error') return null;

  const d = state.data || {};
  const inSync = d.in_sync !== false && (d.drift || []).length === 0;
  const driftCount = (d.drift || []).length;
  const mtime = d.snapshot_mtime_iso
    ? new Date(d.snapshot_mtime_iso).toLocaleString()
    : '—';

  return (
    <div
      className={`mb-6 rounded-sm border px-4 py-3 flex items-start sm:items-center gap-3 text-sm ${
        inSync
          ? 'bg-[#F5F9F3] border-[#84A98C]/40 text-[#354F52]'
          : 'bg-[#FCF6EA] border-[#D4AF37]/60 text-[#354F52]'
      }`}
      data-testid="snapshot-status-bar"
    >
      {inSync ? (
        <CheckCircle2 className="w-5 h-5 text-[#84A98C] shrink-0 mt-0.5" strokeWidth={1.5} />
      ) : (
        <AlertTriangle className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
      )}
      <div className="flex-1 min-w-0">
        {inSync ? (
          <>
            <p className="font-medium">Your edits are ready for the next deploy.</p>
            <p className="text-xs text-[#84A98C] mt-0.5">
              {d.snapshot_product_count} products in the deploy snapshot · last updated {mtime}.
              Redeploy to push everything live on whisperingwristwear.com.
            </p>
          </>
        ) : (
          <>
            <p className="font-medium">
              {driftCount} edit{driftCount === 1 ? '' : 's'} not yet captured in the deploy snapshot.
            </p>
            <p className="text-xs text-[#52796F] mt-0.5">
              Click <b>Sync now</b>, wait a second, then deploy.
              These changes will not reach the live site until the snapshot is rebuilt.
            </p>
          </>
        )}
      </div>
      {!inSync && (
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#354F52] text-[#FAF9F6] hover:bg-[#52796F] disabled:opacity-60 text-xs uppercase tracking-widest"
          data-testid="snapshot-sync-now-btn"
        >
          {syncing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      )}
    </div>
  );
}
